/**
 * The version-history dialog (TMMAPP-0103, audit A4 §5, P:1944-2004), shared by an item's history
 * and the standing summary's: 640 wide, "Version history", a row per version (a 46px mono "v3"
 * label, the text at 14, a 12px meta line of actor · date, the reason at 12px `text2` and a tag pill
 * at the end), the append-only footnote and a contained Close. It only reads: nothing here can
 * rewrite a version (MM-D-64).
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Pill } from '@/components/Pill';
import type { Tone } from '@/theme/statusTones';
import { resolveTokens } from '@/theme/theme';

export interface VersionRow {
  key: string;
  version: number | null;
  text: string;
  /** Actor · date, already formatted. */
  meta: string;
  reason?: string | null;
  tag?: { label: string; tone: Tone };
}

interface VersionHistoryDialogProps {
  titleId: string;
  rows: readonly VersionRow[];
  onClose: () => void;
}

export function VersionHistoryDialog({ titleId, rows, onClose }: VersionHistoryDialogProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: { width: 640, maxWidth: 'calc(100% - 32px)' } } }}
    >
      <DialogTitle id={titleId}>{t('items.historyTitle')}</DialogTitle>
      <DialogContent>
        <Stack sx={{ mt: 0.5 }}>
          {rows.map((row, index) => (
            <Box
              key={row.key}
              sx={{
                display: 'flex',
                gap: 1.5,
                py: 1.5,
                borderTop: index === 0 ? 0 : `1px solid ${tokens.line}`,
              }}
            >
              <Typography
                variant="mono"
                sx={{ width: 46, flexShrink: 0, fontSize: 12, color: tokens.text2 }}
              >
                {row.version === null ? '' : t('items.versionLabel', { version: row.version })}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {row.text}
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised') }}>
                  {row.meta}
                </Typography>
                {row.reason !== undefined && row.reason !== null && row.reason.length > 0 && (
                  <Typography sx={{ fontSize: 12, color: tokens.text2 }}>{row.reason}</Typography>
                )}
              </Box>
              {row.tag !== undefined && (
                <Box sx={{ flexShrink: 0 }}>
                  <Pill tone={row.tag.tone}>{row.tag.label}</Pill>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
        <Typography
          sx={{
            mt: 1,
            pt: 1.5,
            borderTop: `1px solid ${tokens.line}`,
            fontSize: 12,
            color: tokens.textLabel('surfaceRaised'),
          }}
        >
          {t('items.historyFootnote')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
